import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api, ApiError } from '~/lib/api-client';
import { documentKeys, useDocumentDetail } from '~/hooks/queries/use-documents';
import { artifactChangeKeys, useDocumentLastChange, type DocumentLastChange } from '~/hooks/queries/use-artifact-changes';
import { attentionKeys } from '~/lib/query-keys';
import { changesAvailability, type ChangesAvailability } from './changes-gate';

export interface DocumentChanges {
  availability: ChangesAvailability;
  data: DocumentLastChange | undefined;
  refetch: () => void;
  /** FRONT-3313: put EDDY's previous version of the document back. */
  restorePrevious: { run: () => void; pending: boolean };
}

/**
 * FRONT-2669 — the last-change answer for a document, keyed by the document's
 * CURRENT `updatedAt` (read from the same detail query the viewer uses, so a
 * refetch after an edit changes the key and re-asks). `filename` null = the active
 * item is not a document. The panel (the toggle) and the viewer (the Changes view)
 * both call this; they share one cache entry.
 */
export function useDocumentChanges(filename: string | null): DocumentChanges {
  const doc = useDocumentDetail(filename);
  const q = useDocumentLastChange(filename, doc.data?.updatedAt);
  const qc = useQueryClient();
  const baseUpdatedAt = doc.data?.updatedAt;
  const restore = useMutation({
    // `baseUpdatedAt` is the version the person is looking at: the server
    // refuses (409) if the document changed since, rather than overwriting it.
    mutationFn: () =>
      api.post<{ updatedAt: string }>(`/api/artifacts/document/${encodeURIComponent(filename!)}/restore-previous`, {
        baseUpdatedAt,
      }),
    onSuccess: () => {
      toast.success('Restored the previous version.');
      if (filename) void qc.invalidateQueries({ queryKey: documentKeys.detail(filename) });
      void qc.invalidateQueries({ queryKey: documentKeys.all });
      void qc.invalidateQueries({ queryKey: artifactChangeKeys.documentLastChanges() });
      // The restore changes what the sidebar counts as out of date (FRONT-1488 guard).
      void qc.invalidateQueries({ queryKey: attentionKeys.all });
    },
    onError: (err) => {
      toast.error(err instanceof Error && err.message ? err.message : "Couldn't restore the previous version.");
      // 409: the document changed since the person opened it. Reload the
      // current version so the panel shows it and a second press carries the
      // fresh base time instead of repeating the refusal.
      if (err instanceof ApiError && err.status === 409) {
        if (filename) void qc.invalidateQueries({ queryKey: documentKeys.detail(filename) });
        void qc.invalidateQueries({ queryKey: artifactChangeKeys.documentLastChanges() });
      }
    },
  });
  return {
    availability: changesAvailability({ isDocument: filename !== null, isPending: q.isPending, isError: q.isError, data: q.data }),
    data: q.data,
    refetch: () => void q.refetch(),
    restorePrevious: {
      // A click is ignored while a restore is already running, and until the
      // version the person is looking at is known (the server needs it).
      run: () => {
        if (!restore.isPending && filename && baseUpdatedAt) restore.mutate();
      },
      pending: restore.isPending,
    },
  };
}

/** What a text source without version history (a Markdown file) hands the viewer. */
export const NO_DOCUMENT_CHANGES: DocumentChanges = { availability: 'not-a-document', data: undefined, refetch: () => {}, restorePrevious: { run: () => {}, pending: false } };
